import {
  getDocumentNotificationClientId,
  isDocumentOrFolderNotification,
  normalizeDocumentCategoryFromNotification,
} from "./notificationNavigation";
import {
  DOC_TYPE_K1_PACKAGE,
  DOC_TYPE_FILED_COPIES,
  K1_PACKAGE_PURPOSE_TAB,
  FILED_COPIES_PURPOSE_TAB,
} from "./moveDocumentScope";
import { parseDirectoryDetails } from "./documentFolderScope";

export const DOCUMENT_TAB_KEYS = [
  "received",
  "sent",
  "office",
  FILED_COPIES_PURPOSE_TAB,
  K1_PACKAGE_PURPOSE_TAB,
  "requested",
];

export function emptyDocumentTabUnreadCounts() {
  return {
    received: 0,
    sent: 0,
    office: 0,
    [FILED_COPIES_PURPOSE_TAB]: 0,
    [K1_PACKAGE_PURPOSE_TAB]: 0,
    requested: 0,
  };
}

/** Tab totals and per-folder maps from getUserDocs / getDocumentTabUnreadCounts (keep separate). */
export function normalizeDocumentTabUnreadApiData(raw) {
  const data = raw?.data ?? raw ?? {};
  const counts = emptyDocumentTabUnreadCounts();

  const countsSource = data.unread_counts ?? data;
  DOCUMENT_TAB_KEYS.forEach((key) => {
    if (countsSource[key] != null) {
      counts[key] = Number(countsSource[key]) || 0;
    }
  });

  const byFolderRaw = data.by_folder ?? data.byFolder ?? {};
  const byFolder = {};
  const hasPerTabMaps = DOCUMENT_TAB_KEYS.some(
    (key) =>
      byFolderRaw[key] &&
      typeof byFolderRaw[key] === "object" &&
      !Array.isArray(byFolderRaw[key]),
  );

  if (hasPerTabMaps) {
    DOCUMENT_TAB_KEYS.forEach((key) => {
      byFolder[key] =
        byFolderRaw[key] && typeof byFolderRaw[key] === "object"
          ? { ...byFolderRaw[key] }
          : {};
    });
  } else if (byFolderRaw && typeof byFolderRaw === "object") {
    // Flat folder_id → count map: folder highlighting only, never added to tab totals.
    byFolder.received = { ...byFolderRaw };
    DOCUMENT_TAB_KEYS.filter((key) => key !== "received").forEach((key) => {
      byFolder[key] = {};
    });
  } else {
    DOCUMENT_TAB_KEYS.forEach((key) => {
      byFolder[key] = {};
    });
  }

  return { counts, byFolder };
}

export function getFolderUnreadFromApiPayload(apiPayload, folderId, tab) {
  if (!apiPayload?.byFolder || folderId == null || folderId === "") return null;
  const tabKey = tab || "received";
  const tabMap = apiPayload.byFolder[tabKey];
  if (!tabMap || typeof tabMap !== "object") return 0;
  const value = tabMap[String(folderId)] ?? tabMap[folderId];
  return value == null ? 0 : Number(value) || 0;
}

/** Parse getUserDocs response — files list + optional unread_counts / by_folder. */
export function parseGetUserDocsResponse(apiResponse) {
  const body = apiResponse?.data ?? apiResponse ?? {};
  const inner = body.data;
  const files = Array.isArray(inner)
    ? inner
    : Array.isArray(inner?.files)
      ? inner.files
      : Array.isArray(inner?.documents)
        ? inner.documents
        : [];

  const unread_counts = body.unread_counts ?? inner?.unread_counts ?? null;
  const by_folder = body.by_folder ?? inner?.by_folder ?? null;

  return { files, unread_counts, by_folder };
}

export function extractUserFilesList(payload) {
  if (Array.isArray(payload)) return payload;
  return payload?.files ?? [];
}

export function buildDocsTabUnreadPayload(unread_counts, by_folder) {
  if (unread_counts == null && by_folder == null) return null;
  return normalizeDocumentTabUnreadApiData({
    data: { unread_counts, by_folder },
  });
}

function isStaffRole(role) {
  const r = String(role || "").toLowerCase();
  return r === "employee" || r === "manager" || r === "admin";
}

/** Who uploaded the file/folder (from notification payload when backend sends it). */
function normalizeUploaderRole(notify) {
  const raw = String(
    notify?.user_role ??
      notify?.uploaded_by_role ??
      notify?.uploader_role ??
      notify?.initiated_by_role ??
      "",
  ).toLowerCase();
  if (raw === "client") return "client";
  if (isStaffRole(raw) || raw === "employee" || raw === "staff") return "staff";
  return null;
}

/**
 * Tab badges are for the recipient only — not the person who uploaded.
 * Employee upload → client sees count; client upload → staff sees count.
 */
export function isIncomingDocumentNotificationForUser(notify, userRole) {
  const role = String(
    userRole || localStorage.getItem("user_role") || "",
  ).toLowerCase();
  const loggedInId = localStorage.getItem("user_id");

  const uploaderId =
    notify?.uploaded_by ??
    notify?.uploaded_by_id ??
    notify?.created_by ??
    notify?.initiated_by_id ??
    notify?.employee_id;
  if (uploaderId && loggedInId && String(uploaderId) === String(loggedInId)) {
    return false;
  }

  const uploaderRole = normalizeUploaderRole(notify);
  if (uploaderRole) {
    if (role === "client") return uploaderRole === "staff";
    if (isStaffRole(role)) return uploaderRole === "client";
    return false;
  }

  const docType = String(notify?.doc_type ?? "").toLowerCase();
  if (role === "client") {
    return docType !== "client";
  }
  if (isStaffRole(role)) {
    return docType === "client";
  }
  return false;
}

/** Which documents sub-tab a notification belongs on for the logged-in user. */
export function resolveDocumentTabFromNotification(notifyData, userRole) {
  const explicit = String(
    notifyData?.documents_tab ?? notifyData?.documentsTab ?? "",
  )
    .trim()
    .toLowerCase();

  if (explicit === "k1_package") return K1_PACKAGE_PURPOSE_TAB;
  if (explicit === "filed_copies") return FILED_COPIES_PURPOSE_TAB;
  if (DOCUMENT_TAB_KEYS.includes(explicit)) return explicit;

  const docType = String(notifyData?.doc_type ?? "").toLowerCase();
  if (docType === DOC_TYPE_K1_PACKAGE) return K1_PACKAGE_PURPOSE_TAB;
  if (docType === DOC_TYPE_FILED_COPIES) return FILED_COPIES_PURPOSE_TAB;
  if (docType === "office") return "office";

  const role = String(
    userRole || localStorage.getItem("user_role") || "",
  ).toLowerCase();

  // Incoming uploads: map doc_type to the tab where the recipient sees the file.
  if (role === "client") {
    if (docType === "emp") return "received";
    if (docType === "client") return "sent";
  } else {
    if (docType === "client") return "received";
    if (docType === "emp") return "sent";
  }

  return "received";
}

export function notificationMatchesDocumentScope(
  notify,
  { userRole, clientId, documentCategory, clientBusinessId } = {},
) {
  if (!isDocumentOrFolderNotification(notify)) return false;
  if (String(notify?.view_status || "") !== "Not Viewed") return false;

  const notifClientId = getDocumentNotificationClientId(notify, userRole);
  if (clientId != null && clientId !== "") {
    if (String(notifClientId) !== String(clientId)) return false;
  }

  if (documentCategory) {
    const notifCategory = normalizeDocumentCategoryFromNotification(notify);
    if (notifCategory !== documentCategory) return false;
  }

  if (clientBusinessId != null && clientBusinessId !== "") {
    const notifBusinessId =
      notify?.client_business_id ??
      notify?.notification_data?.client_business_id;
    if (
      notifBusinessId != null &&
      notifBusinessId !== "" &&
      String(notifBusinessId) !== String(clientBusinessId)
    ) {
      return false;
    }
  }

  if (!isIncomingDocumentNotificationForUser(notify, userRole)) return false;

  return true;
}

export function countDocumentTabUnread(notifications, scope) {
  const counts = emptyDocumentTabUnreadCounts();
  (notifications || []).forEach((item) => {
    if (!notificationMatchesDocumentScope(item, scope)) return;
    const tab = resolveDocumentTabFromNotification(item, scope?.userRole);
    if (Object.prototype.hasOwnProperty.call(counts, tab)) {
      counts[tab] += 1;
    } else {
      counts.received += 1;
    }
  });
  return counts;
}

export function getDocumentNotificationsForTab(notifications, tab, scope) {
  return (notifications || []).filter((item) => {
    if (!notificationMatchesDocumentScope(item, scope)) return false;
    return resolveDocumentTabFromNotification(item, scope?.userRole) === tab;
  });
}

function normalizeFolderLabel(value) {
  return String(value || "").trim().toLowerCase();
}

/** Folder id from explicit notification fields only (no inference). */
export function getNotificationFolderId(notify) {
  if (!notify) return null;
  const type = String(notify.notification_type || "").toLowerCase();

  if (type === "folder") {
    return (
      notify.notification_item_id ??
      notify.client_folder_id ??
      notify.folder_id ??
      null
    );
  }

  return (
    notify.client_folder_id ??
    notify.folder_id ??
    notify.parent_folder_id ??
    notify.notification_folder_id ??
    null
  );
}

export function getNotificationDocumentId(notify) {
  if (!notify) return null;
  const type = String(notify.notification_type || "").toLowerCase();
  if (type !== "document") return null;
  return notify.notification_item_id ?? notify.client_document_id ?? null;
}

export function getNotificationFolderName(notify) {
  if (!notify) return null;
  return (
    notify.folder_name ??
    notify.parent_folder_name ??
    notify.directory_name ??
    null
  );
}

export function getNotificationPathSegments(notify) {
  if (!notify) return [];
  const raw =
    notify.path ??
    notify.folder_path ??
    notify.file_path ??
    notify.document_path ??
    notify.key_path ??
    notify.s3_path ??
    "";
  const text = String(raw).trim().replace(/^\/+/, "").replace(/\/+$/, "");
  if (!text) return [];
  return text.split("/").filter(Boolean);
}

/**
 * Resolve which folder row a notification belongs to.
 * Falls back to loaded files, folder names, path segments, and enriched API data.
 */
export function resolveNotificationFolderId(
  notify,
  { files = [], folders = [], enriched = {} } = {},
) {
  if (!notify) return null;

  const notifId = String(notify.notification_id ?? "");
  if (notifId && enriched[notifId]) {
    return String(enriched[notifId]);
  }

  const explicit = getNotificationFolderId(notify);
  if (explicit != null && explicit !== "") return String(explicit);

  const type = String(notify.notification_type || "").toLowerCase();

  if (type === "folder") {
    const folderId = notify.notification_item_id ?? notify.client_folder_id;
    if (folderId != null && folderId !== "") return String(folderId);
    const fname = getNotificationFolderName(notify);
    if (fname && folders.length) {
      const match = folders.find(
        (f) => normalizeFolderLabel(f.folder_name) === normalizeFolderLabel(fname),
      );
      if (match?.client_folder_id != null) return String(match.client_folder_id);
    }
  }

  if (type === "document") {
    const docId =
      getNotificationDocumentId(notify) ?? notify.notification_item_id;
    if (docId != null && files.length) {
      const file = files.find(
        (f) => String(f.client_document_id) === String(docId),
      );
      if (file) {
        const fromFile = file.client_folder_id ?? file.folder_id;
        if (fromFile != null && fromFile !== "") return String(fromFile);
        const details = parseDirectoryDetails(file.directory_details);
        for (let i = details.length - 1; i >= 0; i--) {
          const seg = details[i];
          if (
            seg?.id != null &&
            seg.id !== 0 &&
            normalizeFolderLabel(seg.name) !== "home"
          ) {
            return String(seg.id);
          }
        }
      }
    }

    const msg = String(notify.message ?? notify.notification_message ?? "");
    if (msg && folders.length) {
      const byLength = [...folders].sort(
        (a, b) => (b.folder_name?.length || 0) - (a.folder_name?.length || 0),
      );
      for (const folder of byLength) {
        const name = String(folder.folder_name || "").trim();
        if (name.length >= 2 && msg.includes(name)) {
          return String(folder.client_folder_id);
        }
      }
    }

    const segments = getNotificationPathSegments(notify);
    if (segments.length && folders.length) {
      for (const segment of segments) {
        const match = folders.find(
          (f) =>
            normalizeFolderLabel(f.folder_name) === normalizeFolderLabel(segment),
        );
        if (match?.client_folder_id != null) {
          return String(match.client_folder_id);
        }
      }
    }
  }

  return null;
}

function matchesTab(item, tab, scope) {
  if (!tab) return true;
  return resolveDocumentTabFromNotification(item, scope?.userRole) === tab;
}

function folderMatchContext(context) {
  return {
    files: context?.files || [],
    folders: context?.folders || [],
    enriched: context?.enriched || {},
  };
}

/** Per-folder unread counts for the current tab (folder_id → count). */
export function buildFolderUnreadCounts(
  notifications,
  scope,
  tab = null,
  context = {},
) {
  const ctx = folderMatchContext(context);
  const map = {};
  (notifications || []).forEach((item) => {
    if (!notificationMatchesDocumentScope(item, scope)) return;
    if (!matchesTab(item, tab, scope)) return;
    const folderId = resolveNotificationFolderId(item, ctx);
    if (folderId == null || folderId === "") return;
    map[folderId] = (map[folderId] || 0) + 1;
  });
  return map;
}

export function getDocumentNotificationsForFolder(
  notifications,
  folderId,
  scope,
  tab = null,
  context = {},
) {
  const fid = String(folderId);
  const ctx = folderMatchContext(context);
  return (notifications || []).filter((item) => {
    if (!notificationMatchesDocumentScope(item, scope)) return false;
    if (!matchesTab(item, tab, scope)) return false;
    const resolved = resolveNotificationFolderId(item, ctx);
    return resolved != null && String(resolved) === fid;
  });
}

export function getDocumentNotificationsForDocument(
  notifications,
  documentId,
  scope,
  tab = null,
) {
  const did = String(documentId);
  return (notifications || []).filter((item) => {
    if (!notificationMatchesDocumentScope(item, scope)) return false;
    if (!matchesTab(item, tab, scope)) return false;
    const notifDocId = getNotificationDocumentId(item);
    return notifDocId != null && String(notifDocId) === did;
  });
}
